import { useEffect, useRef } from 'react'
import { Pressable, Text, View } from 'react-native'
import { Stack, usePathname, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'

import { RetrySignOutErrorWall } from '../components/ui/RetrySignOutErrorWall'
import { ScreenLoadingFallback } from '../components/ui/ScreenLoadingFallback'
import { useResolvedAuthState } from '../hooks/useResolvedAuthState'

export function RootNavigator() {
  const { t } = useTranslation(['common'])
  const { back, canGoBack, replace } = useRouter()
  const pathname = usePathname()
  const isCallbackRoute = pathname === '/callback'
  const {
    error,
    hasAuthUser,
    isAuthenticated,
    isAuthLoading,
    authorize,
    clearSession,
    dismissInvalidSessionMessage,
    isInvalidSessionError,
  } = useResolvedAuthState()
  const hasNavigatedFromCallbackRef = useRef(false)
  const goBackOrToOnboarding = () => {
    if (canGoBack()) {
      back()
      return
    }

    replace('/(public)/onboarding')
  }

  useEffect(() => {
    if (!isCallbackRoute) {
      hasNavigatedFromCallbackRef.current = false
      return
    }

    if (isAuthLoading || error != null || hasNavigatedFromCallbackRef.current) {
      return
    }

    hasNavigatedFromCallbackRef.current = true
    replace(
      isAuthenticated ? '/(protected)/auth-complete' : '/(public)/onboarding',
    )
  }, [error, isAuthLoading, isAuthenticated, isCallbackRoute, replace])

  if (isAuthLoading && !isCallbackRoute) {
    return <ScreenLoadingFallback />
  }

  if (isInvalidSessionError) {
    return (
      <View className="flex-1 justify-center items-center px-6">
        <Text className="text-lg text-destructive mb-2.5">
          {t('common:session_expired_title')}
        </Text>
        <Text className="text-sm text-gray-600 text-center mb-8">
          {t('common:session_expired_message')}
        </Text>
        <Pressable
          className="bg-primary px-6 py-3 rounded-lg w-full items-center"
          onPress={dismissInvalidSessionMessage}
        >
          <Text className="text-white font-semibold text-base">
            {t('common:session_expired_continue')}
          </Text>
        </Pressable>
      </View>
    )
  }

  if (error != null) {
    return (
      <RetrySignOutErrorWall
        message={error.message}
        onRetry={authorize}
        secondaryActionLabel={
          hasAuthUser ? undefined : t('common:error_go_back')
        }
        onSignOut={hasAuthUser ? clearSession : goBackOrToOnboarding}
      />
    )
  }

  return (
    <Stack screenOptions={{ headerShown: false }}>
      <Stack.Protected guard={isAuthenticated}>
        <Stack.Screen name="index" />
        <Stack.Screen name="(protected)" />
      </Stack.Protected>
      <Stack.Protected guard={!isAuthenticated}>
        <Stack.Screen name="(public)/onboarding" />
      </Stack.Protected>
      <Stack.Screen name="callback" />
      <Stack.Screen name="+not-found" />
    </Stack>
  )
}
